import { Ionicons } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { Link, router, useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from "react-native-reanimated";
import { useDispatch } from "react-redux";

import Loader from "@/components/Loader";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import InputChooseList from "@/components/input/InputChooseList";
import WarningModal from "@/components/modal/utils/WarningModal";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { baseLayer2AssetNet, sendRequest } from "@/utils/ApiUtil";
import { CryptoProfileRequest } from "@/utils/dto/accounts.request";
import {
  AccountAssetsCryptoOptions,
  BlockchainCryptoOptions,
  CounterPartyAccountCRYPTOWalletType,
} from "@/utils/dto/extracted_enums";
import { blockchainAssets } from "@/utils/transactions/ExchangeCurrencies";

type FormData = {
  walletType: string;
  blockchainAddress: string;
  blockchain: string;
  asset: string;
};

export default function CryptoWallet() {
  const [currentTransactionId, setCurrentTransactionId] = useState("");
  const [blockchain, setBlockchain] = useState("");
  const [asset, setAsset] = useState("");
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [blocked, setBlocked] = useState(true);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);

  const walletAddressOpacity = useSharedValue(0);

  const resolver = classValidatorResolver(CryptoProfileRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, formState, reset } = useForm<FormData>({
    mode: "onSubmit",
    resolver,
  });

  const { isSubmitting } = formState;

  useFocusEffect(
    React.useCallback(() => {
      const assetTypeId = blockchain + baseLayer2AssetNet + asset;
      setShowWarningModal(false);
      if (
        currentTransactionId !== globalValues.currentTransactionId ||
        globalValues.currentTransactionId === ""
      ) {
        reset();
        setBlockchain("");
        setAsset("");
        setBlocked(true);
        setCurrentTransactionId(globalValues.currentTransactionId);
        walletAddressOpacity.value = withTiming(0, {
          duration: 500,
          easing: Easing.inOut(Easing.ease),
        });
      }
      if (globalValues.accountsData && blockchain !== "" && asset !== "") {
        const accounts = globalValues.accountsData.filter(
          (account: any) =>
            account.assetTypeId === assetTypeId && account.status === "OPEN"
        );
        if (accounts.length > 0) {
          walletAddressOpacity.value = withTiming(1, {
            duration: 500,
            easing: Easing.inOut(Easing.ease),
          });
          setBlocked(false);
        } else {
          setShowWarningModal(true);
        }
      }
      return () => {
        setShowWarningModal(false);
      };
    }, [
      globalValues.currentTransactionId,
      globalValues.accountsData,
      currentTransactionId,
      blockchain,
      asset,
    ])
  );

  function getFilteredCryptoOptions(
    blockchainType: keyof typeof blockchainAssets
  ) {
    const assets = blockchainAssets[blockchainType];
    enum nullValues {
      default = "",
    }

    if (!assets) {
      return { nullValues };
    }

    const filteredOptions = Object.fromEntries(
      Object.entries(AccountAssetsCryptoOptions).filter(([key, value]) =>
        assets.includes(value)
      )
    );

    return { filteredOptions };
  }

  const onSubmit = async (data: FormData) => {
    const assetTypeId = blockchain + baseLayer2AssetNet + asset;
    const requestData = {
      counterpartyType: globalValues.counterpartyType,
      rails: [globalValues.rail],
      profile: {
        name: globalValues.cryptoProfile.name,
        relationshipToCustomer:
          globalValues.cryptoProfile.relationshipToCustomer,
        profileType: globalValues.cryptoProfile.profileType,
        address: {
          countryCode: globalValues.cryptoProfile.countryCode,
        },
      },
      walletInformation: {
        walletType: data.walletType,
        blockchainAddress: data.blockchainAddress,
        assetTypeId,
      },
    };
    if (data.walletType === "INSTITUTION") {
      router.push("home/transaction/crypto/institutionaddress");
      dispatch(setForm({ cryptoCounterpartyRequest: requestData }));
      return;
    }
    try {
      await sendRequest("POST", "/counterparty", requestData, true);
      router.push("home/transaction/counterparty");
    } catch (error: any) {
      console.log(error);
    }
  };

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: walletAddressOpacity.value,
    };
  }, []);

  return (
    <ScrollView contentContainerClassName="flex-grow items-center bg-white1 p-4 lg:p-0">
      <Link href="/home/transaction/crypto/profile" asChild>
        <View className="absolute top-0 left-0 m-1.5 z-50">
          <Ionicons name="chevron-back-outline" size={28} color="#BDBDBD" />
        </View>
      </Link>
      <Link href="/home" asChild>
        <View className="absolute top-0 right-0 m-1 z-50">
          <Ionicons name="close-outline" size={32} color="#BDBDBD" />
        </View>
      </Link>
      <View className="my-2">
        <Title text={t("home/profile:titleinformation")} />
      </View>
      <View className="z-50 flex-grow flex-col w-full items-center justify-center">
        <View className="z-50">
          <InputChoose
            label={t("home/account:crypto.input.wallettype")}
            control={control}
            name="walletType"
            data={{ CounterPartyAccountCRYPTOWalletType }}
          />
        </View>
        <View className="z-40">
          <InputChooseList
            label={t("home/account:crypto.input.network")}
            name="network"
            control={control}
            handleChange={(value) => setBlockchain(value)}
            data={{ BlockchainCryptoOptions }}
          />
        </View>
        <View className="z-30">
          <InputChoose
            label={t("home/account:crypto.input.asset")}
            name="asset"
            control={control}
            handleChange={(value) => setAsset(value)}
            data={getFilteredCryptoOptions(
              blockchain as keyof typeof blockchainAssets
            )}
          />
        </View>
        <Animated.View style={animatedStyle} className="z-20">
          <Input
            label={t("home/account:crypto.input.walletaddress")}
            name="blockchainAddress"
            control={control}
          />
        </Animated.View>
      </View>
      <Button
        label={t("common:button.nextstep")}
        onClick={handleSubmit(onSubmit)}
        blocked={blocked}
      />
      <WarningModal
        text={t("home/account:crypto.hasnotaccountwithasset")}
        onClose={() => setShowWarningModal(false)}
        onClick={() => setShowWarningModal(false)}
        visible={showWarningModal}
      />
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
